/**
 * VerificationDetailPage
 * ======================
 * Buyer-facing view of one async review they submitted. Renders the
 * verdict (when ready), the expert's attached deliverables, and a
 * rating prompt once a verdict exists.
 *
 * Route: /verifications/:verificationId
 * Auth:  the buyer who submitted this review (ProtectedRoute).
 *
 * BR-011 Tranche 3.2 — completes the buyer side of the loop. Until now
 * the buyer paid → got a verdict email → had nowhere on the app to read
 * the full verdict. This page is that surface.
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Sparkles,
  FileText,
  Star,
  Send,
  Loader2,
  AlertTriangle,
  ShieldCheck,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { verificationsApi } from "@/api/verifications";
import StarPicker from "@/components/StarPicker";
import MarketingNav from "@/components/MarketingNav";

// ---------------------------------------------------------------------------
// Star picker — five stars, hover-preview, click-to-set
// ---------------------------------------------------------------------------

// StarPicker extracted to `@/components/StarPicker` so SessionReviewPage
// can share it. Re-imported below in the imports block.

// ---------------------------------------------------------------------------
// Verdict body — Markdown-ish render (the Tranche 2.3 schema prefix
// folds in here so we want to keep formatting recognisable but not
// run a full Markdown parser). We split on blank lines and render
// each chunk as a <p>, with simple **bold** support.
// ---------------------------------------------------------------------------

function renderInline(text) {
  // Replace **bold** with <strong>. Done in a simple split so we don't
  // pull in a parser for the single formatting feature we use.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const m = p.match(/^\*\*(.+)\*\*$/);
    if (m) return <strong key={i} className="text-ink">{m[1]}</strong>;
    return <span key={i}>{p}</span>;
  });
}

function VerdictBody({ text }) {
  if (!text) return null;
  const blocks = text.split(/\n{2,}/);
  return (
    <div className="space-y-3 text-slateline-600 text-sm leading-relaxed">
      {blocks.map((block, i) => {
        // Bullet block?
        if (block.split("\n").every((line) => line.trim().startsWith("- "))) {
          return (
            <ul key={i} className="list-disc pl-5 space-y-1">
              {block.split("\n").map((line, j) => (
                <li key={j}>{renderInline(line.trim().replace(/^- /, ""))}</li>
              ))}
            </ul>
          );
        }
        // Horizontal rule?
        if (block.trim() === "---") {
          return <hr key={i} className="border-slateline-200" />;
        }
        return (
          <p key={i} className="whitespace-pre-wrap">
            {renderInline(block)}
          </p>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function VerificationDetailPage() {
  const { verificationId } = useParams();
  const navigate = useNavigate();

  const [verdict, setVerdict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Rating form state
  const [ratingValue, setRatingValue] = useState(null);
  const [ratingText, setRatingText] = useState("");
  const [submittingRating, setSubmittingRating] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await verificationsApi.getVerdict(verificationId);
        setVerdict(data);
        if (data.buyer_rating) {
          setRatingValue(data.buyer_rating);
          setRatingText(data.buyer_rating_text || "");
        }
      } catch (err) {
        setError(err.response?.data?.detail || "Couldn't load this review.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [verificationId]);

  const submitRating = async () => {
    if (!ratingValue) {
      toast.error("Pick a star rating first.");
      return;
    }
    setSubmittingRating(true);
    try {
      await verificationsApi.submitRating(verificationId, {
        rating: ratingValue,
        text: ratingText.trim() || undefined,
      });
      toast.success("Thanks for rating this verdict.");
      setVerdict((prev) =>
        prev
          ? { ...prev, buyer_rating: ratingValue, buyer_rating_text: ratingText.trim() }
          : prev
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save your rating.");
    } finally {
      setSubmittingRating(false);
    }
  };

  // ── Loading / error ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-canvas">
        <MarketingNav />
        <div className="max-w-2xl mx-auto px-4 pt-32 text-center">
          <AlertTriangle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
          <p className="text-slateline-500 mb-4">{error}</p>
          <Button onClick={() => navigate("/dashboard")} variant="outline">
            Back
          </Button>
        </div>
      </div>
    );
  }

  // ── Verdict not yet submitted ────────────────────────────────────────────

  if (!verdict?.submitted) {
    return (
      <div className="min-h-screen bg-canvas">
        <MarketingNav />
        <div className="max-w-2xl mx-auto px-4 pt-28 pb-16">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-slateline-500 hover:text-ink mb-8 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-canvas-card border border-slateline-200 rounded-sm p-8 text-center"
          >
            <Clock className="w-12 h-12 text-brand-green mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-ink mb-2">
              Your review is in progress
            </h2>
            <p className="text-slateline-500 mb-6">
              The expert is reviewing your submission. You'll get an email
              the moment the verdict is ready.
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  // ── Verdict ready: render verdict + attachments + rating prompt ──────────

  const fileAttachments = (verdict.attachments || []).filter(
    (a) => a?.url
  );
  const alreadyRated = !!verdict.buyer_rating;

  return (
    <div className="min-h-screen bg-canvas">
      <MarketingNav />
      <div className="max-w-3xl mx-auto px-4 pt-28 pb-16">

        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-slateline-500 hover:text-ink mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex items-center gap-3"
        >
          <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-brand-green" />
          </div>
          <div>
            <h1 className="font-display text-3xl text-ink leading-tight">
              Verdict <em className="not-italic font-bold text-brand-green italic">ready</em>
            </h1>
            <p className="text-slateline-500 text-sm">
              Submitted {new Date(verdict.created_at).toLocaleDateString()}
            </p>
          </div>
        </motion.div>

        {/* Confidence pill */}
        {verdict.confidence_level && (
          <div className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-brand-green/10 border border-brand-green/30">
            <Sparkles className="w-4 h-4 text-brand-green" />
            <span className="text-brand-green text-xs font-semibold uppercase tracking-widest">
              Confidence: {verdict.confidence_level}
            </span>
          </div>
        )}

        {/* Verdict body */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-canvas-card border border-slateline-200 rounded-sm p-6 mb-6"
        >
          <h2 className="text-xs uppercase tracking-widest text-brand-green font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            The verdict
          </h2>
          <VerdictBody text={verdict.verdict_text} />
        </motion.div>

        {/* Key findings */}
        {verdict.key_findings && verdict.key_findings.length > 0 && (
          <div className="bg-canvas-card border border-slateline-200 rounded-sm p-6 mb-6">
            <h2 className="text-xs uppercase tracking-widest text-brand-green font-semibold mb-4">
              Key findings
            </h2>
            <ul className="space-y-2">
              {verdict.key_findings.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slateline-600">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-green flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action items */}
        {verdict.action_items && verdict.action_items.length > 0 && (
          <div className="bg-canvas-card border border-slateline-200 rounded-sm p-6 mb-6">
            <h2 className="text-xs uppercase tracking-widest text-brand-green font-semibold mb-4">
              Recommended action items
            </h2>
            <ol className="space-y-2 list-decimal pl-5">
              {verdict.action_items.map((a, i) => (
                <li key={i} className="text-sm text-slateline-600">
                  {a}
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Expert attachments */}
        {fileAttachments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-brand-greenPale/40 border border-brand-green/30 rounded-sm p-6 mb-6"
          >
            <h2 className="text-xs uppercase tracking-widest text-brand-green font-semibold mb-4 flex items-center gap-2">
              <Download className="w-4 h-4" />
              Deliverables from the expert
            </h2>
            <ul className="space-y-2">
              {fileAttachments.map((a) => (
                <li
                  key={a.url}
                  className="flex items-center gap-3 p-3 bg-canvas border border-slateline-200 rounded-md"
                >
                  <span className="text-brand-green text-lg" aria-hidden>
                    {a.ext === "pdf" ? "📄"
                     : ["png","jpg","jpeg","webp","gif"].includes(a.ext) ? "🖼"
                     : ["js","ts","py","html","css","json","yaml","md"].includes(a.ext) ? "💻"
                     : "📎"}
                  </span>
                  <span className="flex-1 truncate text-ink text-sm">{a.name}</span>
                  <span className="text-xs text-slateline-400">
                    {a.size ? `${Math.max(1, Math.round(a.size / 1024))} KB` : ""}
                  </span>
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand-green hover:text-brand-greenHover text-sm font-medium"
                  >
                    Download
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Rating prompt */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-canvas-card border border-slateline-200 rounded-sm p-6"
        >
          <h2 className="text-xs uppercase tracking-widest text-brand-green font-semibold mb-2 flex items-center gap-2">
            <Star className="w-4 h-4" />
            {alreadyRated ? "Your rating" : "Rate this verdict"}
          </h2>
          <p className="text-slateline-500 text-sm mb-4">
            {alreadyRated
              ? "You rated this verdict. You can update your rating any time."
              : "How was the expert's verdict? Your rating helps us match buyers with the right experts."}
          </p>
          <StarPicker
            value={ratingValue}
            onChange={setRatingValue}
            disabled={submittingRating}
          />
          <Textarea
            value={ratingText}
            onChange={(e) => setRatingText(e.target.value)}
            placeholder="Optional: what worked, what didn't?"
            className="mt-4 bg-canvas border-slateline-200 min-h-[80px] focus:border-brand-green"
          />
          <Button
            onClick={submitRating}
            disabled={submittingRating || !ratingValue}
            className="mt-4 bg-brand-green hover:bg-brand-greenHover text-white"
          >
            {submittingRating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {alreadyRated ? "Update rating" : "Submit rating"}
              </>
            )}
          </Button>
          {alreadyRated && verdict.buyer_rated_at && (
            <p className="text-xs text-slateline-400 mt-2">
              Last rated {new Date(verdict.buyer_rated_at).toLocaleString()}
            </p>
          )}
        </motion.div>

        {/* Footer ack */}
        <div className="mt-8 flex items-center gap-2 text-xs text-slateline-400">
          <CheckCircle2 className="w-4 h-4 text-brand-green" />
          <span>This verdict is private. Only you and your expert can see it.</span>
        </div>
      </div>
    </div>
  );
}
